import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, SectionTitle, TimeAgo } from "@/components/sov";
import StarMap from "@/components/StarMap";
import {
  Link2, Terminal, Cloud, Anchor, Plus, Copy, Check, AlertTriangle,
  Globe, Radio, Heart,
} from "lucide-react";

const TABS = [
  { id: "url",          label: "URL Scanner",   icon: Link2,    sub: "Passive recon" },
  { id: "shell-sentry", label: "Shell-Sentry",  icon: Terminal, sub: "Curl beacon for internal hosts" },
  { id: "cloud",        label: "Cloud Bridge",  icon: Cloud,    sub: "AWS · Azure · GCP" },
];

const HULL_COLOR = {
  url: "#dcb95f",
  "shell-sentry": "#6cc88f",
  cloud: "#5b88c2",
};

const heartbeatStatus = (iso) => {
  if (!iso) return { color: "#9bb1c8", label: "no signal" };
  const age = (Date.now() - new Date(iso).getTime()) / 1000;
  if (age < 90)    return { color: "#6cc88f", label: "live" };
  if (age < 600)   return { color: "#dcb95f", label: "fading" };
  return { color: "#ff7a6e", label: "silent" };
};

const Copyable = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };
  return (
    <div className="flex items-stretch border border-[color:var(--gold-line)] bg-[var(--obsidian-1)]">
      <pre
        className="flex-1 p-3 text-[11px] font-mono text-gold-100 overflow-x-auto whitespace-pre-wrap break-all"
        data-testid={testid}
      >{value}</pre>
      <button
        onClick={copy}
        className="px-3 border-l border-[color:var(--gold-line)] hover:bg-[rgba(200,162,75,0.08)]"
        data-testid={`${testid}-copy`}
        title="Copy"
      >
        {copied ? <Check size={12} className="text-[#6cc88f]" /> : <Copy size={12} className="text-gold-300" />}
      </button>
    </div>
  );
};

const UrlScannerForm = ({ onIngest }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/intake/url-scan", { url });
      onIngest(data.hull);
      setUrl("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="url-form">
      <div>
        <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Target URL</label>
        <input
          data-testid="url-input"
          required
          className="sov-input mt-1 font-mono"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <div className="font-mono text-[9px] text-ivory-3 mt-1.5">
          Passive only — fetches headers, robots.txt, security.txt, scores security-headers presence.
          Internal/private/loopback targets are blocked.
        </div>
      </div>
      {err && <div className="font-mono text-[10px]" style={{ color: "#ff7a6e" }}>{err}</div>}
      <button type="submit" disabled={busy || !url} className="btn-gold" data-testid="url-submit">
        <Link2 size={11} />
        {busy ? "Scanning…" : "Strike Target"}
      </button>
    </form>
  );
};

const ShellSentryForm = ({ onIssue }) => {
  const [label, setLabel] = useState("");
  const [exp, setExp] = useState(60);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOut(null);
    try {
      const { data } = await api.post("/intake/shell-sentry/generate",
                                       { label, expires_minutes: parseInt(exp, 10) });
      setOut(data);
      onIssue();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="sentry-form">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Hull Label</label>
            <input
              data-testid="sentry-label"
              required
              className="sov-input mt-1"
              placeholder="e.g. CFO Workstation · Prod Bastion"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">TTL (min)</label>
            <input
              data-testid="sentry-ttl"
              type="number" min="5" max="1440"
              className="sov-input mt-1"
              value={exp}
              onChange={(e) => setExp(e.target.value)}
            />
          </div>
        </div>
        {err && <div className="font-mono text-[10px]" style={{ color: "#ff7a6e" }}>{err}</div>}
        <button type="submit" disabled={busy || !label} className="btn-gold" data-testid="sentry-submit">
          <Terminal size={11} />
          {busy ? "Forging…" : "Issue Beacon Token"}
        </button>
      </form>

      {out && (
        <div className="space-y-4 border-t border-[color:var(--gold-line)] pt-5" data-testid="sentry-output">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Single-use until</div>
              <div className="font-display text-[15px] text-gold-100 mt-0.5">
                <TimeAgo iso={out.expires_at} /> · <span className="text-ivory-2">{out.expires_minutes}m</span>
              </div>
            </div>
            <span className="font-mono text-[10px] tracking-[0.16em] text-gold-200 uppercase">
              {out.wayfinder_id}
            </span>
          </div>

          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-1.5">
              Bash / Linux / macOS
            </div>
            <Copyable value={out.one_liner_bash} testid="sentry-bash" />
          </div>
          <div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-1.5">
              PowerShell / Windows
            </div>
            <Copyable value={out.one_liner_powershell} testid="sentry-pwsh" />
          </div>
          <div className="font-serif italic text-[12px] text-ivory-2 leading-snug">
            "Run on the target host. The Vessel records hostname, OS, IP, user — single beacon per token, then sealed."
          </div>
        </div>
      )}
    </div>
  );
};

const CloudBridgeForm = ({ onIngest }) => {
  const [provider, setProvider] = useState("aws");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { provider, label: form.label || `${provider}-default`, region: form.region || null };
      if (provider === "aws") {
        payload.aws_access_key_id = form.aws_access_key_id;
        payload.aws_secret_access_key = form.aws_secret_access_key;
      } else if (provider === "azure") {
        payload.azure_tenant_id = form.azure_tenant_id;
        payload.azure_client_id = form.azure_client_id;
        payload.azure_client_secret = form.azure_client_secret;
      } else {
        payload.gcp_service_account_json = form.gcp_service_account_json;
      }
      const { data } = await api.post("/intake/cloud-bridge", payload);
      onIngest(data.hull);
      setForm({});
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="cloud-form">
      <div className="flex items-center gap-2">
        {["aws", "azure", "gcp"].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setProvider(p)}
            className={`btn-ghost ${provider === p ? "ring-1 ring-[color:var(--gold-300)] text-gold-100" : ""}`}
            data-testid={`cloud-provider-${p}`}
          >
            {p.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Label</label>
          <input data-testid="cloud-label" required className="sov-input mt-1"
                 placeholder="e.g. Prod / Finance Tenant"
                 value={form.label || ""} onChange={(e) => set("label", e.target.value)} />
        </div>
        <div>
          <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Region (optional)</label>
          <input data-testid="cloud-region" className="sov-input mt-1"
                 placeholder={provider === "aws" ? "us-east-1" : provider === "azure" ? "eastus" : "us-central1"}
                 value={form.region || ""} onChange={(e) => set("region", e.target.value)} />
        </div>
      </div>

      {provider === "aws" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Access Key ID</label>
            <input className="sov-input mt-1 font-mono" required value={form.aws_access_key_id || ""}
                   onChange={(e) => set("aws_access_key_id", e.target.value)} data-testid="aws-key" />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Secret Access Key</label>
            <input className="sov-input mt-1 font-mono" type="password" required
                   value={form.aws_secret_access_key || ""}
                   onChange={(e) => set("aws_secret_access_key", e.target.value)} data-testid="aws-secret" />
          </div>
        </div>
      )}
      {provider === "azure" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Tenant ID</label>
            <input className="sov-input mt-1 font-mono" required value={form.azure_tenant_id || ""}
                   onChange={(e) => set("azure_tenant_id", e.target.value)} />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Client ID</label>
            <input className="sov-input mt-1 font-mono" required value={form.azure_client_id || ""}
                   onChange={(e) => set("azure_client_id", e.target.value)} />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Client Secret</label>
            <input className="sov-input mt-1 font-mono" type="password" required
                   value={form.azure_client_secret || ""}
                   onChange={(e) => set("azure_client_secret", e.target.value)} />
          </div>
        </div>
      )}
      {provider === "gcp" && (
        <div>
          <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">
            Service Account JSON
          </label>
          <textarea
            data-testid="gcp-sa"
            className="sov-input mt-1 font-mono"
            rows={6}
            required
            placeholder='{"type":"service_account","project_id":"...","client_email":"..."}'
            value={form.gcp_service_account_json || ""}
            onChange={(e) => set("gcp_service_account_json", e.target.value)}
          />
        </div>
      )}

      <div className="flex items-center gap-2 px-3 py-2 border border-[color:var(--gold-line)] bg-[rgba(220,185,95,0.04)]">
        <AlertTriangle size={11} className="text-gold-200 shrink-0" />
        <span className="font-mono text-[10px] tracking-[0.1em] text-ivory-2">
          Credentials are Fernet-encrypted at rest. Mock metadata is ingested (real provider SDK calls deferred).
        </span>
      </div>

      {err && <div className="font-mono text-[10px]" style={{ color: "#ff7a6e" }}>{err}</div>}

      <button type="submit" disabled={busy} className="btn-gold" data-testid="cloud-submit">
        <Cloud size={11} />
        {busy ? "Bridging…" : "Bridge Cloud"}
      </button>
    </form>
  );
};

const IntakeHull = () => {
  const [tab, setTab] = useState("url");
  const [hulls, setHulls] = useState([]);
  const [counts, setCounts] = useState({ total: 0, url: 0, "shell-sentry": 0, cloud: 0 });
  const [starmap, setStarmap] = useState({ nodes: [], edges: [], stats: { wayfinders: 0, hulls: 0 } });
  const [wf, setWf] = useState(null);

  const refresh = async () => {
    try {
      const [h, sm, w] = await Promise.all([
        api.get("/intake/hulls?limit=80"),
        api.get("/intake/star-map"),
        api.get("/intake/wayfinder"),
      ]);
      setHulls(h.data.hulls);
      setCounts(h.data.counts);
      setStarmap(sm.data);
      setWf(w.data);
    } catch {}
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 12000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6" data-testid="intake-page">
      <SectionTitle sub="// CONNECT NEW HULL · ENCRYPTED INGEST">
        Intake Hull
      </SectionTitle>

      {/* Wayfinder card */}
      {wf && (
        <div className="sov-panel sov-corners p-5 flex items-center justify-between" data-testid="wayfinder-card">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />
          <div className="flex items-center gap-4">
            <Anchor size={22} className="text-gold-300" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Your Wayfinder ID</div>
              <div className="font-display text-[18px] tracking-[0.18em] text-gold-100 mt-0.5">
                {wf.wayfinder_id}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Hulls under your command</div>
            <div className="font-display text-[22px] text-gold-100">{wf.hull_count}</div>
          </div>
        </div>
      )}

      {/* TABS */}
      <Panel
        testid="panel-intake-modes"
        title="Three Modes"
        code="§ VIII.A"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3">
            URL · Shell-Sentry · Cloud Bridge
          </span>
        }
      >
        <div className="grid grid-cols-3 border-b border-[color:var(--gold-line)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 p-4 text-left border-b-2 transition-all ${tab === t.id ? "border-[var(--gold-300)] bg-[rgba(200,162,75,0.06)]" : "border-transparent hover:bg-[rgba(200,162,75,0.03)]"}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={16} className={tab === t.id ? "text-gold-100" : "text-gold-300"} />
              <div>
                <div className={`font-display text-[13px] tracking-[0.16em] ${tab === t.id ? "text-gold-100" : "text-ivory-2"}`}>
                  {t.label}
                </div>
                <div className="font-mono text-[9px] tracking-[0.14em] text-ivory-3 mt-0.5 uppercase">
                  {t.sub}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="p-6">
          {tab === "url"          && <UrlScannerForm onIngest={refresh} />}
          {tab === "shell-sentry" && <ShellSentryForm onIssue={refresh} />}
          {tab === "cloud"        && <CloudBridgeForm onIngest={refresh} />}
        </div>
      </Panel>

      {/* STAR MAP */}
      <Panel
        testid="panel-starmap"
        title="Star-Map"
        code="§ VIII.B"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <Globe size={11} className="text-gold-300" />
            {starmap.stats.wayfinders} wayfinders · {starmap.stats.hulls} hulls
          </span>
        }
      >
        <div className="p-4">
          <StarMap data={starmap} />
        </div>
      </Panel>

      {/* HULL ROSTER + HEARTBEAT */}
      <Panel
        testid="panel-hulls-roster"
        title="Hull Roster"
        code="§ VIII.C"
        action={
          <div className="flex items-center gap-3">
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3">
              {counts.url} URL · {counts["shell-sentry"]} Sentry · {counts.cloud} Cloud
            </span>
          </div>
        }
      >
        <table className="sov-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Label</th>
              <th>Wayfinder</th>
              <th>Operator</th>
              <th>Detail</th>
              <th>Heartbeat</th>
              <th>Boarded</th>
            </tr>
          </thead>
          <tbody>
            {hulls.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-ivory-3 italic">
                No hulls yet — issue a strike from a mode above.
              </td></tr>
            )}
            {hulls.map((h) => {
              const color = HULL_COLOR[h.hull_type] || "#dcb95f";
              const hb = heartbeatStatus(h.ingested_at);
              return (
                <tr key={h.id} data-testid={`hull-row-${h.id.slice(0,6)}`}>
                  <td>
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase px-1.5 py-px border"
                          style={{ color, borderColor: `${color}55` }}>
                      {h.hull_type}
                    </span>
                  </td>
                  <td className="text-gold-100">{h.label}</td>
                  <td className="text-gold-200">{h.wayfinder_id}</td>
                  <td className="text-ivory-2">{h.operator_email}</td>
                  <td className="text-ivory-3 font-mono text-[10px]">
                    {h.summary?.host && <span>{h.summary.host}</span>}
                    {h.summary?.hostname && <span>{h.summary.hostname} · {h.summary.ip}</span>}
                    {h.summary?.provider && <span>{h.summary.provider} · {h.summary?.fingerprint}</span>}
                    {typeof h.summary?.risk_score === "number" && (
                      <span className="ml-2" style={{ color: h.summary.risk_score >= 50 ? "#ff7a6e" : "var(--gold-200)" }}>
                        risk {h.summary.risk_score}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase"
                          style={{ color: hb.color }}>
                      <Heart size={10} fill={hb.color} stroke="none" />
                      {hb.label}
                    </span>
                  </td>
                  <td><TimeAgo iso={h.ingested_at} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>
    </div>
  );
};

export default IntakeHull;
